import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Edit, Star, Trophy, TrendingUp, Loader2, Facebook, Instagram, Twitter, Linkedin, MessageCircle, Edit2, HelpCircle, User, MapPin, Heart, Target, Bell, Lock, Globe, ChevronRight, LogOut, Settings as SettingsIcon, Video, Bot, Users, MessageSquare, Activity, PlayCircle, Moon, Sun, Download, Share2 } from "lucide-react";
import RecentActivity from "@/components/RecentActivity";
import { supabase } from "@/integrations/supabase/client";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import ManageSkillsDialog from "@/components/ManageSkillsDialog";
import EditSocialMediaDialog from "@/components/EditSocialMediaDialog";
import EditProfileDialog from "@/components/EditProfileDialog";
import LogoutConfirmDialog from "@/components/LogoutConfirmDialog";
import { ProfileGalleryDialog } from "@/components/ProfileGalleryDialog";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import { SkillResumeCard } from "@/components/SkillResumeCard";
import { PrivacyPolicyDialog, TermsOfServiceDialog, UserResponsibilitiesDialog, DisclaimersDialog } from "@/components/LegalContentDialogs";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useSkills } from "@/hooks/useSkills";
import { useAchievements } from "@/hooks/useAchievements";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useVideoCallContext } from "@/components/VideoCallProvider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const Profile = () => {
  const { user, signOut } = useAuth();
  const [searchParams] = useSearchParams();
  const viewingUserId = searchParams.get('id');
  const isOwnProfile = !viewingUserId || viewingUserId === user?.id;
  
  const { profile, loading: profileLoading } = useProfile(viewingUserId || undefined);
  const { teachingSkills, learningSkills, loading: skillsLoading } = useSkills(viewingUserId || undefined);
  const { userAchievements } = useAchievements(viewingUserId || undefined);
  const { isSupported: pushSupported, isSubscribed: pushSubscribed, isLoading: pushLoading, subscribe: subscribePush, unsubscribe: unsubscribePush } = usePushNotifications();
  const { startCall, isLoading: isCallLoading } = useVideoCallContext();
  
  const [manageSkillsOpen, setManageSkillsOpen] = useState(false);
  const [socialMediaOpen, setSocialMediaOpen] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [publicProfile, setPublicProfile] = useState(true);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return document.documentElement.classList.contains('dark');
    }
    return false;
  });
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleStartVideoCall = async () => {
    if (!viewingUserId) return;
    await startCall(viewingUserId);
  };

  // Handle dark mode toggle
  const handleDarkModeToggle = (enabled: boolean) => {
    setDarkMode(enabled);
    if (enabled) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
    toast({
      title: enabled ? "Dark mode enabled" : "Light mode enabled",
      description: "Your theme preference has been saved.",
    });
  };

  const handleSignOut = async () => {
    await signOut();
    toast({
      title: "Signed out successfully",
      description: "See you next time!",
    });
    navigate("/auth");
  };

  const handleDeleteAccount = async () => {
    try {
      const currentUser = (await supabase.auth.getUser()).data.user;
      
      if (!currentUser) {
        throw new Error("No user found");
      }

      // Delete user profile and related data
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', currentUser.id);

      if (profileError) throw profileError;

      // Sign out the user
      await signOut();

      toast({
        title: "Account deleted",
        description: "Your account has been permanently deleted.",
      });
      
      navigate("/");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete account. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (profileLoading || skillsLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pb-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <Navigation />
      </div>
    );
  }

  const displayUserId = viewingUserId || user?.id;
  const userName = profile?.name || (isOwnProfile ? user?.user_metadata?.name : null) || "New User";
  const userAvatar = profile?.profile_image_1 || profile?.avatar_url || (isOwnProfile ? user?.user_metadata?.avatar_url : null) || `https://api.dicebear.com/7.x/avataaars/svg?seed=${displayUserId}`;
  const userBio = profile?.bio || (isOwnProfile ? "Content creator learning new skills daily 🚀" : "No bio yet");

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background pb-24">
      {/* Header with Profile */}
      <header className="gradient-primary text-white p-6 pb-12 rounded-b-3xl shadow-strong">
        <div className="container max-w-md mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <div className="relative">
              <img 
                src={userAvatar}
                alt={userName}
                onClick={() => setShowGallery(true)}
                className="w-20 h-20 rounded-full border-4 border-white/30 shadow-lg cursor-pointer hover:opacity-90 transition-opacity"
              />
              {isOwnProfile && (
                <button
                  onClick={() => setEditProfileOpen(true)}
                  className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white text-primary flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
                >
                  <Edit2 className="h-3 w-3" />
                </button>
              )}
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <User className="h-5 w-5" />
                {userName}
              </h1>
              
              {/* Social Media Icons - Horizontal beneath name */}
              {isOwnProfile ? (
                <div className="flex gap-2 mt-1 mb-2">
                  <button
                    onClick={() => profile?.facebook_url ? window.open(profile.facebook_url, '_blank') : setSocialMediaOpen(true)}
                    className={`transition-opacity ${profile?.facebook_url ? 'opacity-100' : 'opacity-30'}`}
                  >
                    <Facebook className="h-4 w-4 text-white" fill={profile?.facebook_url ? "#1877F2" : "currentColor"} />
                  </button>
                  <button
                    onClick={() => profile?.instagram_url ? window.open(profile.instagram_url, '_blank') : setSocialMediaOpen(true)}
                    className={`transition-opacity ${profile?.instagram_url ? 'opacity-100' : 'opacity-30'}`}
                  >
                    <Instagram className="h-4 w-4 text-white" style={profile?.instagram_url ? { color: "#E4405F" } : {}} />
                  </button>
                  <button
                    onClick={() => profile?.twitter_url ? window.open(profile.twitter_url, '_blank') : setSocialMediaOpen(true)}
                    className={`transition-opacity ${profile?.twitter_url ? 'opacity-100' : 'opacity-30'}`}
                  >
                    <Twitter className="h-4 w-4 text-white" fill={profile?.twitter_url ? "#1DA1F2" : "currentColor"} />
                  </button>
                  <button
                    onClick={() => profile?.linkedin_url ? window.open(profile.linkedin_url, '_blank') : setSocialMediaOpen(true)}
                    className={`transition-opacity ${profile?.linkedin_url ? 'opacity-100' : 'opacity-30'}`}
                  >
                    <Linkedin className="h-4 w-4 text-white" fill={profile?.linkedin_url ? "#0A66C2" : "currentColor"} />
                  </button>
                  <button
                    onClick={() => profile?.snapchat_url ? window.open(profile.snapchat_url, '_blank') : setSocialMediaOpen(true)}
                    className={`transition-opacity ${profile?.snapchat_url ? 'opacity-100' : 'opacity-30'}`}
                  >
                    <MessageCircle className="h-4 w-4 text-white" style={profile?.snapchat_url ? { color: "#FFFC00" } : {}} />
                  </button>
                </div>
              ) : (
                <div className="flex gap-2 mt-1 mb-2">
                  {profile?.facebook_url && (
                    <button onClick={() => window.open(profile.facebook_url, '_blank')}>
                      <Facebook className="h-4 w-4 text-white" fill="#1877F2" />
                    </button>
                  )}
                  {profile?.instagram_url && (
                    <button onClick={() => window.open(profile.instagram_url, '_blank')}>
                      <Instagram className="h-4 w-4 text-white" style={{ color: "#E4405F" }} />
                    </button>
                  )}
                  {profile?.twitter_url && (
                    <button onClick={() => window.open(profile.twitter_url, '_blank')}>
                      <Twitter className="h-4 w-4 text-white" fill="#1DA1F2" />
                    </button>
                  )}
                  {profile?.linkedin_url && (
                    <button onClick={() => window.open(profile.linkedin_url, '_blank')}>
                      <Linkedin className="h-4 w-4 text-white" fill="#0A66C2" />
                    </button>
                  )}
                  {profile?.snapchat_url && (
                    <button onClick={() => window.open(profile.snapchat_url, '_blank')}>
                      <MessageCircle className="h-4 w-4 text-white" style={{ color: "#FFFC00" }} />
                    </button>
                  )}
                </div>
              )}
              
              <p className="text-white/90 text-sm">{userBio}</p>
            </div>
          </div>


          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center">
              <Star className="h-5 w-5 mx-auto mb-1 fill-white" />
              <p className="text-2xl font-bold">{profile?.rating.toFixed(1) || '0.0'}</p>
              <p className="text-xs text-white/80">Rating</p>
            </div>
            
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center">
              <Trophy className="h-5 w-5 mx-auto mb-1" />
              <p className="text-2xl font-bold">{profile?.sessions_completed || 0}</p>
              <p className="text-xs text-white/80">Sessions</p>
            </div>

            <Tooltip>
              <TooltipTrigger asChild>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center cursor-help">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <TrendingUp className="h-5 w-5" />
                    <HelpCircle className="h-3 w-3 opacity-70" />
                  </div>
                  <p className="text-2xl font-bold">{profile?.renaissance_score || 0}</p>
                  <p className="text-xs text-white/80">Score</p>
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="font-semibold mb-2">How SkillSwipe Score Works:</p>
                <ul className="text-sm space-y-1">
                  <li>📚 <strong>Complete Sessions:</strong> +10 points</li>
                  <li>⭐ <strong>Get Rated:</strong> +5 points per star</li>
                  <li>🎯 <strong>Teach Skills:</strong> +15 points</li>
                  <li>🏆 <strong>Unlock Achievements:</strong> +20 points</li>
                  <li>❤️ <strong>Showcase Impressed:</strong> +2 points per impression</li>
                </ul>
              </TooltipContent>
            </Tooltip>
          </div>

          {/* Action Buttons for Other User's Profile */}
          {!isOwnProfile && viewingUserId && (
            <div className="flex gap-3 mt-4">
              <Button 
                className="flex-1 bg-white/20 hover:bg-white/30 text-white border-0"
                onClick={() => navigate(`/chat?userId=${viewingUserId}`)}
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                Message
              </Button>
              <Button 
                className="flex-1 bg-white text-primary hover:bg-white/90"
                onClick={handleStartVideoCall}
                disabled={isCallLoading}
              >
                {isCallLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Video className="h-4 w-4 mr-2" />
                )}
                Video Call
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="container max-w-md mx-auto px-4 -mt-6 space-y-6">
        {/* Skills Portfolio */}
        <Card className="p-6 shadow-soft">
          <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
            <span className="text-xl">🎯</span>
            Skills Portfolio
          </h3>
          
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold">I can teach</p>
                <Badge variant="secondary">{teachingSkills.length} skills</Badge>
              </div>
              {teachingSkills.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {teachingSkills.map((item) => (
                    <Badge key={item.id} className="bg-primary">
                      {item.skill.emoji} {item.skill.name}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No teaching skills yet</p>
              )}
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold">I want to learn</p>
                <Badge variant="secondary">{learningSkills.length} skills</Badge>
              </div>
              {learningSkills.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {learningSkills.map((item) => (
                    <Badge key={item.id} variant="outline">
                      {item.skill.emoji} {item.skill.name}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No learning goals yet</p>
              )}
            </div>
          </div>
          
          {isOwnProfile && (
            <div className="space-y-2 mt-4">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => setManageSkillsOpen(true)}
              >
                <Edit className="mr-2 h-4 w-4" />
                Manage Skills
              </Button>
              <SkillResumeCard 
                profile={{
                  name: userName,
                  bio: userBio,
                  avatar_url: userAvatar,
                  rating: profile?.rating,
                  sessions_completed: profile?.sessions_completed,
                  renaissance_score: profile?.renaissance_score,
                  level: profile?.level,
                }}
                teachingSkills={teachingSkills}
                learningSkills={learningSkills}
                userAvatar={userAvatar}
                userId={user?.id || ''}
              />
            </div>
          )}
        </Card>

        <ManageSkillsDialog 
          open={manageSkillsOpen}
          onOpenChange={setManageSkillsOpen}
        />


        <EditProfileDialog
          open={editProfileOpen}
          onOpenChange={setEditProfileOpen}
        />

        <EditSocialMediaDialog 
          open={socialMediaOpen}
          onOpenChange={setSocialMediaOpen}
        />

        <ProfileGalleryDialog
          open={showGallery}
          onClose={() => setShowGallery(false)}
          images={[profile?.profile_image_1, profile?.profile_image_2, profile?.profile_image_3]}
          userName={userName}
        />

        <LogoutConfirmDialog
          open={logoutDialogOpen}
          onOpenChange={setLogoutDialogOpen}
          onConfirm={handleSignOut}
        />

        {/* Achievements */}
        <Card className="p-6 shadow-soft">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="h-5 w-5 text-accent-orange" />
            <h3 className="font-bold text-lg">Achievements</h3>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-5 w-5 p-0">
                  <HelpCircle className="h-4 w-4 text-muted-foreground" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="font-semibold mb-2">Unlock Epic Badges! 🔥</p>
                <ul className="text-sm space-y-1">
                  <li>🎯 <strong>First W</strong> - Complete your first sesh</li>
                  <li>🔥 <strong>On Fire</strong> - Hit that 10 session streak</li>
                  <li>⭐ <strong>5-Star Vibes</strong> - Get rated 5 stars 5 times</li>
                  <li>🎓 <strong>Knowledge Dealer</strong> - Teach 5 different skills</li>
                  <li>🧠 <strong>Always Learning</strong> - Learn 10 new skills</li>
                  <li>💯 <strong>Legend Status</strong> - Reach level 10</li>
                </ul>
              </TooltipContent>
            </Tooltip>
          </div>
          
          {userAchievements.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                {userAchievements.slice(0, 4).map((item) => (
                  <div 
                    key={item.id}
                    className="p-4 bg-secondary rounded-lg text-center hover:scale-105 transition-transform cursor-pointer"
                  >
                    <span className="text-3xl mb-2 block">{item.achievement.icon}</span>
                    <p className="text-xs font-semibold mb-1">{item.achievement.title}</p>
                    <p className="text-xs text-muted-foreground">{item.achievement.description}</p>
                  </div>
                ))}
              </div>
              
              {userAchievements.length > 4 && (
                <Button variant="ghost" className="w-full mt-4">
                  View All Achievements ({userAchievements.length})
                </Button>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <Trophy className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Complete sessions to unlock achievements!
              </p>
            </div>
          )}
        </Card>

        {/* Recent Activity */}
        <Card className="p-6 shadow-soft">
          <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            Recent Activity
          </h3>
          <RecentActivity userId={displayUserId} />
        </Card>

        {/* Settings Section - Only visible for own profile */}
        {isOwnProfile && (
          <>
            {/* Profile Settings */}
            <Card className="p-6 space-y-4">
              <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                <SettingsIcon className="h-5 w-5" />
                Settings
              </h3>
              
              <button
                onClick={() => setEditProfileOpen(true)}
                className="w-full flex items-center justify-between p-3 hover:bg-secondary rounded-lg transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Edit className="h-5 w-5 text-primary" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold">Edit Profile</p>
                    <p className="text-sm text-muted-foreground">Update your name, bio, and avatar</p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </button>
            </Card>
            
            <Card className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Preferences</h3>
                
                <div className="space-y-4">
                  {pushSupported && (
                    <>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Bell className="h-5 w-5 text-muted-foreground" />
                          <div>
                            <Label htmlFor="notifications">Push Notifications</Label>
                            <p className="text-sm text-muted-foreground">
                              Receive match and message alerts
                            </p>
                          </div>
                        </div>
                        <Switch
                          id="notifications"
                          checked={pushSubscribed}
                          disabled={pushLoading}
                          onCheckedChange={(checked) => checked ? subscribePush() : unsubscribePush()}
                        />
                      </div>
                      <Separator />
                    </>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Globe className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <Label htmlFor="public-profile">Public Profile</Label>
                        <p className="text-sm text-muted-foreground">
                          Make your profile visible to all users
                        </p>
                      </div>
                    </div>
                    <Switch
                      id="public-profile"
                      checked={publicProfile}
                      onCheckedChange={setPublicProfile}
                    />
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {darkMode ? (
                        <Moon className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <Sun className="h-5 w-5 text-muted-foreground" />
                      )}
                      <div>
                        <Label htmlFor="dark-mode">Dark Mode</Label>
                        <p className="text-sm text-muted-foreground">
                          Toggle dark theme appearance
                        </p>
                      </div>
                    </div>
                    <Switch
                      id="dark-mode"
                      checked={darkMode}
                      onCheckedChange={handleDarkModeToggle}
                    />
                  </div>

                  {!window.matchMedia("(display-mode: standalone)").matches && (
                    <>
                      <Separator />
                      <button 
                        onClick={() => navigate("/install")}
                        className="flex items-center justify-between w-full"
                      >
                        <div className="flex items-center gap-3">
                          <Download className="h-5 w-5 text-muted-foreground" />
                          <div className="text-left">
                            <p className="font-medium">Install App</p>
                            <p className="text-sm text-muted-foreground">
                              Get notifications & quick access
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </Card>

            <Card className="p-6 space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-2">Privacy & Security</h3>
                <div className="space-y-3">
                  <button 
                    onClick={() => setChangePasswordOpen(true)}
                    className="flex items-center gap-3 text-sm hover:text-primary transition-colors"
                  >
                    <Lock className="h-4 w-4" />
                    Change Password
                  </button>
                  
                  <PrivacyPolicyDialog />
                  <TermsOfServiceDialog />
                  <UserResponsibilitiesDialog />
                  <DisclaimersDialog />
                </div>
              </div>
            </Card>

            <ChangePasswordDialog 
              open={changePasswordOpen} 
              onOpenChange={setChangePasswordOpen} 
            />

            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-2">About SkillSwipe</h3>
              <p className="text-sm font-medium text-foreground">
                Swipe. Learn. Grow. Earn.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                You teach me, I teach you. Trade skills with a swipe.
              </p>
              
              <div className="flex flex-col gap-3 mt-4">
                <button 
                  onClick={async () => {
                    const shareData = {
                      title: 'SkillSwipe - Trade Skills with a Swipe',
                      text: 'Swipe. Learn. Grow. Earn. You teach me, I teach you. Trade skills with a swipe! Thank you for building a skilled community 🙌',
                      url: window.location.origin
                    };
                    
                    if (navigator.share) {
                      try {
                        await navigator.share(shareData);
                      } catch (error) {
                        console.error('Error sharing:', error);
                      }
                    } else {
                      await navigator.clipboard.writeText(`${shareData.text} ${shareData.url}`);
                      toast({
                        title: "Link copied!",
                        description: "Share message copied to clipboard",
                      });
                    }
                  }}
                  className="flex items-center gap-2 text-sm text-primary hover:underline"
                >
                  <Share2 className="h-4 w-4" />
                  Share App
                </button>
                
                <button 
                  onClick={() => {
                    localStorage.removeItem('skillswipe_tour_completed');
                    toast({
                      title: "Tour Reset",
                      description: "Visit the Swipe page to see the app tour again!",
                    });
                  }}
                  className="flex items-center gap-2 text-sm text-primary hover:underline"
                >
                  <PlayCircle className="h-4 w-4" />
                  Replay App Tour
                </button>
              </div>
            </Card>

            {/* Logout Section */}
            <Card className="p-4">
              <button
                onClick={() => setLogoutDialogOpen(true)}
                className="w-full flex items-center justify-between p-3 hover:bg-destructive/10 rounded-lg transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-destructive/10 rounded-lg">
                    <LogOut className="h-5 w-5 text-destructive" />
                  </div>
                  <p className="font-semibold text-destructive">Logout</p>
                </div>
                <ChevronRight className="h-5 w-5 text-destructive" />
              </button>
            </Card>

            {/* Danger Zone - Delete Account */}
            <Card className="p-6 border-destructive/50">
              <h3 className="text-lg font-semibold mb-2 text-destructive">Danger Zone</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Once you delete your account, there is no going back. Please be certain.
              </p>
              
              <Dialog>
                <DialogTrigger asChild>
                  <button className="w-full flex items-center justify-center gap-2 p-3 bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-lg transition-colors font-semibold">
                    Delete Account
                  </button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Delete Account</DialogTitle>
                    <DialogDescription>
                      This action cannot be undone. This will permanently delete your account.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="p-4 bg-destructive/10 rounded-lg">
                      <p className="text-sm font-semibold mb-2">This will delete:</p>
                      <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                        <li>Your profile and personal information</li>
                        <li>All your skills and achievements</li>
                        <li>Your session history and ratings</li>
                        <li>All messages and conversations</li>
                      </ul>
                    </div>
                    
                    <div className="flex gap-3 justify-end">
                      <DialogTrigger asChild>
                        <Button variant="outline">Cancel</Button>
                      </DialogTrigger>
                      <Button variant="destructive" onClick={handleDeleteAccount}>
                        Delete My Account
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </Card>
          </>
        )}
      </main>

      <Navigation />
    </div>
    </TooltipProvider>
  );
};

export default Profile;
